import { useEffect, useState, useRef } from 'react';

// Global state to track modal count
let modalCount = 0;
let originalScrollY = 0;
let originalBodyStyle = null;
let scrollPreventionHandler = null;
let isScrollPreventionActive = false;
let activeModals = new Set();

/**
 * Custom hook to prevent background scrolling when modal is open
 * @param {boolean} isOpen - Whether the modal is open
 */
export const usePreventScroll = (isOpen) => {
  useEffect(() => {
    // Generate a unique ID for this modal instance
    const modalId = Math.random().toString(36).substr(2, 9);
    
    // console.log('usePreventScroll called with isOpen:', isOpen, 'modalCount:', modalCount, 'isScrollPreventionActive:', isScrollPreventionActive, 'modalId:', modalId);
    
    if (isOpen) {
      // Add this modal to active modals
      activeModals.add(modalId);
      
      // First modal opening
      if (modalCount === 0) {
        // console.log('First modal opening - preventing scroll');
        originalScrollY = window.scrollY;
        originalBodyStyle = {
          position: document.body.style.position,
          top: document.body.style.top,
          width: document.body.style.width,
          overflow: document.body.style.overflow,
          overscrollBehavior: document.body.style.overscrollBehavior
        };
        
        // Prevent background scrolling
        document.body.style.position = 'fixed';
        document.body.style.top = `-${originalScrollY}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        document.body.style.overscrollBehavior = 'none';
        
        // Add event listeners to prevent background scrolling only
        scrollPreventionHandler = (e) => {
          // Only prevent scrolling if the target is not within a modal content
          const modalContent = document.querySelector('.modal-content');
          if (!modalContent || !modalContent.contains(e.target)) {
            e.preventDefault();
            e.stopPropagation();
          }
        };
        
        // Prevent wheel events on background only
        document.addEventListener('wheel', scrollPreventionHandler, { passive: false });
        document.addEventListener('touchmove', scrollPreventionHandler, { passive: false });
        document.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'PageUp' || e.key === 'PageDown' || e.key === 'Home' || e.key === 'End') {
            // Only prevent keyboard scrolling if not within modal content
            const modalContent = document.querySelector('.modal-content');
            if (!modalContent || !modalContent.contains(e.target)) {
              console.log('Preventing keyboard scroll:', e.key);
              e.preventDefault();
            }
          }
        });
        
        isScrollPreventionActive = true;
      }
      modalCount++;
      console.log('Modal count increased to:', modalCount, 'Active modals:', activeModals.size);
    } else {
      // Remove this modal from active modals
      activeModals.delete(modalId);
      
      // Modal closing
      modalCount = Math.max(0, modalCount - 1);
      console.log('Modal count decreased to:', modalCount, 'Active modals:', activeModals.size);
      
      // Last modal closing
      if (modalCount === 0) {
        console.log('Last modal closing - restoring scroll');
        // Remove event listeners
        if (scrollPreventionHandler) {
          document.removeEventListener('wheel', scrollPreventionHandler);
          document.removeEventListener('touchmove', scrollPreventionHandler);
          scrollPreventionHandler = null;
        }
        
        // Restore scrolling
        document.body.style.position = originalBodyStyle?.position || '';
        document.body.style.top = originalBodyStyle?.top || '';
        document.body.style.width = originalBodyStyle?.width || '';
        document.body.style.overflow = originalBodyStyle?.overflow || '';
        document.body.style.overscrollBehavior = originalBodyStyle?.overscrollBehavior || '';
        window.scrollTo(0, originalScrollY);
        
        isScrollPreventionActive = false;
      }
    }
    
    // Cleanup function to restore scrolling when component unmounts
    return () => {
      // Remove this modal from active modals
      activeModals.delete(modalId);
      modalCount = Math.max(0, modalCount - 1);
      // console.log('Cleanup - modal count decreased to:', modalCount, 'Active modals:', activeModals.size);
      
      // Only restore scroll if this was the last modal AND scroll prevention was active
      if (modalCount === 0 && isScrollPreventionActive && activeModals.size === 0) {
        // console.log('Last modal unmounting - restoring scroll');
        // Remove event listeners
        if (scrollPreventionHandler) {
          document.removeEventListener('wheel', scrollPreventionHandler);
          document.removeEventListener('touchmove', scrollPreventionHandler);
          scrollPreventionHandler = null;
        }
        
        document.body.style.position = originalBodyStyle?.position || '';
        document.body.style.top = originalBodyStyle?.top || '';
        document.body.style.width = originalBodyStyle?.width || '';
        document.body.style.overflow = originalBodyStyle?.overflow || '';
        document.body.style.overscrollBehavior = originalBodyStyle?.overscrollBehavior || '';
        window.scrollTo(0, originalScrollY);
        
        isScrollPreventionActive = false;
      } else if (modalCount > 0 || activeModals.size > 0) {
        // If there are still other modals open, don't restore scroll
        // console.log('Other modals still open, not restoring scroll');
      }
    };
  }, [isOpen]);
};

/**
 * Custom hook to handle clicks outside of a modal/form
 * @param {React.RefObject} ref - Ref to the modal content element
 * @param {Function} onClose - Function to call when clicking outside
 * @param {boolean} isOpen - Whether the modal is open
 */
export const useClickOutside = (ref, onClose, isOpen = true) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [ref, onClose, isOpen]);
};

/**
 * Custom hook for managing dropdown state and functionality
 * @param {Array} options - Array of options for the dropdown
 * @param {string} placeholder - Placeholder text
 * @param {any} defaultValue - Default selected value
 * @param {Function} onSelect - Callback when an option is selected
 */
export const useDropdown = (options, placeholder, defaultValue, onSelect) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(defaultValue || '');
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Update selected value when defaultValue changes
  useEffect(() => {
    if (defaultValue !== undefined) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle option selection
  const handleSelect = (option) => {
    console.log('Dropdown handleSelect:', option);
    setSelectedValue(option.value);
    setSearchTerm('');
    setIsOpen(false);
    onSelect?.(option.value);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Could implement keyboard navigation here
        break;
      case 'ArrowUp':
        e.preventDefault();
        // Could implement keyboard navigation here
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions.length > 0) {
          handleSelect(filteredOptions[0]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return {
    isOpen,
    setIsOpen,
    selectedValue,
    setSelectedValue,
    searchTerm,
    setSearchTerm,
    filteredOptions,
    handleSelect,
    handleKeyDown,
    dropdownRef
  };
};
